import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  type ReactNode,
} from 'react';
import { useLocation } from 'react-router-dom';
import {
  resolveRouteAnimationFeatures,
  resolveRouteAnimationPrefix,
  type AnimationFeature,
} from '@/config/animationRegistry';
import {
  applyAnimationsEnabled,
  getAnimationsEnabled,
  getEffectiveAnimationsEnabled,
  isRouteAnimationsEnabled,
  setAnimationsEnabled,
  setRouteAnimationsEnabled,
  useAnimationsEnabled,
  useRouteAnimationOverrides,
} from '@/utils/animationPreference';

interface AnimationContextValue {
  enabled: boolean;
  setEnabled: (enabled: boolean) => void;
  routeFeatures: AnimationFeature[];
  currentRoutePrefix: string | null;
  pageAnimationsEnabled: boolean;
  routeOverrides: Record<string, boolean>;
  isRouteEnabled: (route: string) => boolean;
  setRouteEnabled: (route: string, enabled: boolean) => void;
  isFeatureActive: (feature: AnimationFeature) => boolean;
}

const AnimationContext = createContext<AnimationContextValue | null>(null);

export function AnimationProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const [enabled, setEnabled] = useAnimationsEnabled();
  const routeOverrides = useRouteAnimationOverrides();

  const currentRoutePrefix = useMemo(
    () => resolveRouteAnimationPrefix(location.pathname),
    [location.pathname]
  );

  const routeFeatures = useMemo(
    () => resolveRouteAnimationFeatures(location.pathname),
    [location.pathname]
  );

  const pageAnimationsEnabled = useMemo(() => {
    if (!enabled) return false;
    if (!currentRoutePrefix) return true;
    return isRouteAnimationsEnabled(currentRoutePrefix, routeOverrides);
  }, [enabled, currentRoutePrefix, routeOverrides]);

  useEffect(() => {
    applyAnimationsEnabled(getEffectiveAnimationsEnabled(location.pathname));
  }, [enabled, location.pathname, routeOverrides]);

  const isRouteEnabled = useCallback(
    (route: string) => isRouteAnimationsEnabled(route, routeOverrides),
    [routeOverrides]
  );

  const setRouteEnabled = useCallback((route: string, on: boolean) => {
    setRouteAnimationsEnabled(route, on);
  }, []);

  const isFeatureActive = useCallback(
    (feature: AnimationFeature) =>
      pageAnimationsEnabled && routeFeatures.includes(feature),
    [pageAnimationsEnabled, routeFeatures]
  );

  const value = useMemo<AnimationContextValue>(
    () => ({
      enabled,
      setEnabled,
      routeFeatures,
      currentRoutePrefix,
      pageAnimationsEnabled,
      routeOverrides,
      isRouteEnabled,
      setRouteEnabled,
      isFeatureActive,
    }),
    [
      enabled,
      setEnabled,
      routeFeatures,
      currentRoutePrefix,
      pageAnimationsEnabled,
      routeOverrides,
      isRouteEnabled,
      setRouteEnabled,
      isFeatureActive,
    ]
  );

  return (
    <AnimationContext.Provider value={value}>{children}</AnimationContext.Provider>
  );
}

/** Global + route-scoped animation gate. Defaults to enabled when outside provider. */
export function useRouteAnimation(feature: AnimationFeature): boolean {
  const ctx = useContext(AnimationContext);
  if (!ctx) {
    return getAnimationsEnabled();
  }
  return ctx.isFeatureActive(feature);
}

export function useAnimationContext(): AnimationContextValue {
  const ctx = useContext(AnimationContext);
  if (!ctx) {
    throw new Error('useAnimationContext must be used within AnimationProvider');
  }
  return ctx;
}

/** Global animation toggle — works with or without provider. */
export function useAnimationToggle(): [boolean, (enabled: boolean) => void] {
  const ctx = useContext(AnimationContext);
  const [fallbackEnabled, setFallback] = useAnimationsEnabled();
  if (ctx) {
    return [ctx.enabled, ctx.setEnabled];
  }
  return [fallbackEnabled, setFallback];
}

/** Effective animation for current page (global + route override). */
export function usePageAnimationsEnabled(): boolean {
  const ctx = useContext(AnimationContext);
  if (!ctx) {
    return getAnimationsEnabled();
  }
  return ctx.pageAnimationsEnabled;
}
